// src/pages/markets/board/board/categories.ts
//
// The 20 things a board pane can be: one per trader group the schema declares,
// plus the two discovery columns. Built from the schema, not hardcoded — the
// group list grows on the indexer, not here.
//
// What a group is CALLED, what colour it carries and which seed column it
// inherits are no longer decided here either: they come from the `board_groups`
// documents, through the narrow `GroupNamer` below. The namer is optional so
// that the structural call sites — slot resolution, filter seeding — do not
// have to carry one; without it a group reads as its humanized key, which is
// wrong-but-safe rather than an internal identifier.

import { columnDefaults } from '../api/useSchema';
import {
    GATED_FIELDS,
    type BoardFilters,
    type BoardSchema,
    type ColumnId,
} from '../api/boardTypes';
import { COLUMN_BY_ID, type ColumnConfig } from './columnConfig';
import { humanize, resolveGroup, type SeedColumn } from '../state/boardGroups';

// What a pane can become: everything a ColumnConfig is except the slot key,
// which is assigned by position rather than by category.
export type CategoryConfig = Omit<ColumnConfig, 'id'>;

export const CONVICTION_SECTION = 'conviction';
const DISCOVERY_SECTION = 'discovery';

// The two things categories.ts needs to know about a group. Structurally
// satisfied by BoardGroupsApi, but stated narrowly so this file stays free of
// React and Firestore.
export interface GroupNamer {
    resolve: (group: string) => { seedColumn: SeedColumn };
    titleOf: (group: string) => string;
    offered?: (group: string) => boolean;
}

// No documents, so: humanized names and the default seed column. The same
// answer the board gives while the collection read is still in flight.
const DEFAULT_NAMER: GroupNamer = {
    resolve: group => resolveGroup(group, undefined),
    titleOf: group => humanize(group),
};

export function gatedNamer(offered: (group: string) => boolean): GroupNamer {
    return { ...DEFAULT_NAMER, offered };
}

// The discovery columns are categories in their own right, with no group.
const DISCOVERY_SOURCES: ColumnId[] = ['new', 'active'];

export function categoryKey(source: ColumnId, group: string | null): string {
    return group ? `group:${group}` : `column:${source}`;
}

// The slot key is not part of a category; strip it so no stray id rides along.
function withoutId(config: ColumnConfig): CategoryConfig {
    const { id: _id, ...rest } = config;
    return rest;
}

// A conviction category inherits its presentation from whichever seed column it
// routes to, so a new group looks like the column it behaves like. Which column
// that is used to be a hardcoded set of two group names; it is now the group's
// own `seed_column`.
function convictionCategory(group: string, namer: GroupNamer): CategoryConfig {
    const source: ColumnId = namer.resolve(group).seedColumn;
    return {
        ...withoutId(COLUMN_BY_ID[source]),
        source,
        group,
        title: namer.titleOf(group),
        section: CONVICTION_SECTION,
    };
}

function discoveryCategory(source: ColumnId): CategoryConfig {
    return {
        ...withoutId(COLUMN_BY_ID[source]),
        source,
        group: null,
        section: DISCOVERY_SECTION,
    };
}

// Titles are resolved here rather than at each display site, so the header, the
// info panel and the picker all read the same name. `categoryKey` keys on
// source and group, never on the title, so renaming a group cannot change what
// a saved profile resolves to.
export function buildCategories(
    schema: BoardSchema | undefined,
    namer: GroupNamer = DEFAULT_NAMER
): CategoryConfig[] {
    if (!schema) return [];
    const conviction = (schema.groups ?? [])
        .filter(g => namer.offered?.(g) ?? true)
        .map(g => convictionCategory(g, namer));
    return [...conviction, ...DISCOVERY_SOURCES.map(discoveryCategory)];
}

export function resolveCategory(
    schema: BoardSchema | undefined,
    source: string,
    group: string | null,
    namer: GroupNamer = DEFAULT_NAMER
): CategoryConfig | null {
    const key = categoryKey(source as ColumnId, group);
    return (
        buildCategories(schema, namer).find(
            c => categoryKey(c.source, c.group) === key
        ) ?? null
    );
}

// The pane a slot falls back to: a stored category the schema no longer lists
// must degrade to a working column, never to a blank one.
//
// A conviction seed carries no group name of its own — it takes the backend's
// default for its column, which is where `insider_gomez` used to be written
// into the frontend as a literal.
export function seedCategory(
    slot: string,
    schema?: BoardSchema
): CategoryConfig {
    const seed = withoutId(COLUMN_BY_ID[slot]);
    if (seed.section !== CONVICTION_SECTION) return seed;
    return { ...seed, group: columnDefaults(schema, seed.source).group };
}

// One precedence for BOTH halves of the pair: the link's value, then the stored
// one, then the schema's default. Resolving with the source from one rung and
// the group from another is what produced this branch's two Critical bugs.
// A group is legitimately null, so each rung is tested with `!== undefined`.
export function resolveSlotCategory(
    schema: BoardSchema | undefined,
    slot: string,
    candidates: {
        linkSource?: string | null;
        linkGroup?: string | null;
        storedSource?: string | null;
        storedGroup?: string | null;
    },
    namer: GroupNamer = DEFAULT_NAMER
): CategoryConfig {
    const seed = COLUMN_BY_ID[slot];
    const source =
        candidates.linkSource ?? candidates.storedSource ?? seed.source;
    const group =
        candidates.linkGroup !== undefined
            ? candidates.linkGroup
            : candidates.storedGroup !== undefined
              ? candidates.storedGroup
              : columnDefaults(schema, source).group;
    return (
        resolveCategory(schema, source, group, namer) ??
        seedCategory(slot, schema)
    );
}

// Switching a pane keeps the reader's work where the new category can still
// use it. The seed decides whether a field exists at all, so a field the new
// category does not declare stays absent rather than arriving as null.
export function carryFilters(
    seed: BoardFilters,
    live: BoardFilters,
    sortKeys: readonly string[]
): BoardFilters {
    const out: BoardFilters = {
        ...seed,
        eventsInclude: live.eventsInclude,
        eventsExclude: live.eventsExclude,
        tagsInclude: live.tagsInclude,
        tagsExclude: live.tagsExclude,
        tagMatch: live.tagMatch,
        sortDir: live.sortDir,
        sortBy: sortKeys.includes(live.sortBy) ? live.sortBy : seed.sortBy,
    };
    const gated: Record<string, unknown> = {};
    for (const field of GATED_FIELDS) {
        if (seed[field] === undefined) continue;
        if (live[field] === undefined) continue;
        gated[field] = live[field];
    }
    return { ...out, ...gated } as BoardFilters;
}
